import type { AudienceContent, AudiencePanel } from '@/content/home';
import { ArrowRight } from '@/components/ui/Icons';

export type AudienceSplitProps = {
  content: AudienceContent;
};

export function AudienceSplit({ content }: AudienceSplitProps) {
  return (
    <section
      aria-labelledby="audience-heading"
      className="bg-canvas px-6 py-[120px] md:px-[72px]"
    >
      <div className="mx-auto flex max-w-[1296px] flex-col items-center gap-16">
        <div className="flex w-full max-w-[760px] flex-col items-center gap-5 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-pencil">
            {content.eyebrow}
          </p>
          <h2
            id="audience-heading"
            className="text-[32px] font-bold leading-[1.15] tracking-[-1px] text-ink md:text-[42px]"
          >
            {content.heading}
          </h2>
          <p className="text-[16px] leading-[1.55] text-graphite md:text-[17px]">
            {content.subhead}
          </p>
        </div>

        <div className="flex w-full flex-col gap-8">
          {content.panels.map((panel, index) => (
            <AudiencePanelRow
              key={panel.id}
              panel={panel}
              reverse={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function AudiencePanelRow({
  panel,
  reverse,
}: {
  panel: AudiencePanel;
  reverse: boolean;
}) {
  const dark = panel.variant === 'dark';
  return (
    <article
      id={panel.id}
      className={[
        'scroll-mt-24 overflow-hidden rounded-[18px]',
        dark ? 'bg-ink' : 'border border-rule bg-page',
      ].join(' ')}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div
          className={[
            'h-[260px] w-full overflow-hidden lg:h-auto',
            reverse ? 'lg:order-2' : 'lg:order-1',
          ].join(' ')}
        >
          <img
            src={panel.screenshot}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
        </div>
        <div
          className={[
            'flex flex-col gap-6 p-8 md:p-12 lg:p-16',
            reverse ? 'lg:order-1' : 'lg:order-2',
          ].join(' ')}
        >
          <p
            className={[
              'text-[11px] font-semibold uppercase tracking-[0.12em]',
              dark ? 'text-erased' : 'text-cloak-accent',
            ].join(' ')}
          >
            {panel.tag}
          </p>
          <h3
            className={[
              'text-[26px] font-bold leading-[1.2] tracking-[-0.6px] md:text-[30px]',
              dark ? 'text-page' : 'text-ink',
            ].join(' ')}
          >
            {panel.heading}
          </h3>
          <p
            className={[
              'text-[15px] leading-[1.6] md:text-[16px]',
              dark ? 'text-erased' : 'text-graphite',
            ].join(' ')}
          >
            {panel.body}
          </p>
          <a
            href={panel.href}
            className={[
              'inline-flex items-center gap-2 self-start text-[14px] font-semibold no-underline',
              dark
                ? 'text-page hover:text-cloak-tint'
                : 'text-cloak-accent hover:text-cloak-accent/80',
            ].join(' ')}
          >
            {panel.link}
            <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </article>
  );
}
